import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface OnboardingTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
}

interface OnboardingTableProps<T> {
  data: T[];
  columns: OnboardingTableColumn<T>[];
  rowKey: (row: T) => string;
  selectedKeys?: Set<string>;
  rowClassName?: (row: T) => string | undefined;
  className?: string;
}

function OnboardingTable<T>({
  data,
  columns,
  rowKey,
  selectedKeys,
  rowClassName,
  className,
}: OnboardingTableProps<T>) {
  return (
    <div
      className={cn(
        'box-border mb-5 overflow-auto rounded-lg border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]',
        className,
      )}>
      <table className="w-full min-w-0 border-collapse text-[0.8125rem]">
        <thead>
          <tr className="border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)]">
            {columns.map(column => (
              <th key={column.key} className="box-border p-0 text-left">
                <div className="flex items-center gap-2 whitespace-nowrap px-3 py-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
                  {column.header}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => {
            const key = rowKey(row);
            const selected = selectedKeys?.has(key);
            return (
              <tr
                key={key}
                className={cn(
                  'group border-0 border-b border-solid border-[var(--inv-border)] transition-colors last:border-b-0',
                  selected
                    ? 'bg-[color-mix(in_srgb,var(--inv-accent)_8%,transparent)] hover:bg-[color-mix(in_srgb,var(--inv-accent)_12%,transparent)]'
                    : 'bg-[var(--inv-surface)] hover:bg-[var(--inv-surface-muted)]',
                  rowClassName?.(row),
                )}>
                {columns.map(column => (
                  <td
                    key={column.key}
                    className={cn(
                      'box-border bg-inherit px-3 py-2 align-middle text-[0.8125rem] text-[var(--inv-text)]',
                      column.className,
                    )}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { OnboardingTable };
